import React, { useState, useEffect, useMemo } from 'react';
import { fetcher, useFlexibleList } from '../../../hooks/useSWR';
import { LockedReferenceDocumentField } from '../../../components/LockedReferenceDocumentField';

const getExpectedModuleNameForType = (typeName) => {
  const mapping = {
    GDP: 'COMPLAINT_GDP',
    GMP: 'COMPLAINT_GMP',
  };
  return mapping[typeName?.toUpperCase()] || null;
};

const formatDocText = (doc) => {
  if (!doc?.code?.trim()) return null;
  const version = doc.currentRevision?.version?.trim();
  return version ? `${doc.code.trim()} - ${version}` : doc.code.trim();
};

/**
 * Reference Document at end of Add/Edit Complaint form.
 * Depends on complaint type (GDP/GMP module). Snapshot stored on create.
 */
const ComplaintReferenceDocument = ({ formData, setFormData, config }) => {
  const canUpdateForm = typeof setFormData === 'function';
  const [isLoading, setIsLoading] = useState(false);

  const { data: complaintTypes = [] } = useFlexibleList(
    config?.api?.lookupEndpoints?.complaintTypes || '/complaint-type',
  );
  const { data: allDocumentModules = [] } = useFlexibleList('/document-module');

  const complaintTypeId = useMemo(() => {
    let id = formData?.complaintTypeId;
    if (typeof id === 'object' && id !== null) id = id.id || id.value;
    if (formData?.complaintType && typeof formData.complaintType === 'object') {
      id = formData.complaintType.id;
    }
    return id ?? null;
  }, [formData?.complaintTypeId, formData?.complaintType]);

  const complaintType = useMemo(() => {
    if (formData?.complaintType && typeof formData.complaintType === 'object') {
      return formData.complaintType;
    }
    return complaintTypes.find((t) => t.id === complaintTypeId) || null;
  }, [formData?.complaintType, complaintTypes, complaintTypeId]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      // View / split panel: never refetch (avoids loading flicker + scroll jump)
      if (!canUpdateForm) {
        setIsLoading(false);
        return;
      }

      // Existing record with snapshot: keep it
      if (formData?.id && formData?.referenceDocument) {
        setIsLoading(false);
        return;
      }

      if (!complaintTypeId || !complaintTypes.length || !allDocumentModules.length) {
        setIsLoading(false);
        return;
      }

      if (!complaintType) {
        setIsLoading(false);
        return;
      }

      const expectedModuleName = getExpectedModuleNameForType(complaintType.name);
      if (!expectedModuleName) {
        setIsLoading(false);
        return;
      }

      const module = allDocumentModules.find(
        (m) =>
          m.name === expectedModuleName ||
          m.name?.toUpperCase() === expectedModuleName.toUpperCase() ||
          (m.name?.includes('GDP') && complaintType.name.toUpperCase() === 'GDP') ||
          (m.name?.includes('GMP') && complaintType.name.toUpperCase() === 'GMP'),
      );

      if (!module) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const documents = await fetcher(
          `/document-management/document?module=${encodeURIComponent(module.name)}`,
        );
        const docsArray = Array.isArray(documents) ? documents : [];
        const text = formatDocText(docsArray[0] ?? null);
        if (cancelled) return;
        setFormData((prev) => {
          // Don't overwrite if editing an existing snapshot
          if (prev?.id && prev?.referenceDocument) return prev;
          if (prev?.referenceDocument === (text || '')) return prev;
          return { ...prev, referenceDocument: text || '' };
        });
      } catch {
        /* keep empty */
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    canUpdateForm,
    formData?.id,
    formData?.referenceDocument,
    complaintTypeId,
    complaintType,
    complaintTypes,
    allDocumentModules,
    setFormData,
  ]);

  // Hide until a complaint type is chosen (create flow)
  if (!complaintTypeId && !formData?.referenceDocument) {
    return null;
  }

  return (
    <LockedReferenceDocumentField
      value={formData?.referenceDocument ?? ''}
      onChange={
        canUpdateForm
          ? (next) => setFormData((prev) => ({ ...prev, referenceDocument: next }))
          : undefined
      }
      isLoading={isLoading}
      recordId={formData?.id}
      inputId="complaint-reference-document"
      emptyHint="No reference document found for this complaint type"
    />
  );
};

export default ComplaintReferenceDocument;
